import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Sparkles, Package } from "lucide-react";
import { toast } from "sonner";

interface MaterialCalculatorProps {
  jobId: string;
  orgId: string;
  totalSquareFeet?: number;
  onOrderCreated?: () => void;
}

export function MaterialCalculator({
  jobId,
  orgId,
  totalSquareFeet = 0,
  onOrderCreated,
}: MaterialCalculatorProps) {
  const [calculating, setCalculating] = useState(false);
  const [materials, setMaterials] = useState<any[]>([]);
  const [supplier, setSupplier] = useState("");

  const calculateMaterials = () => {
    setCalculating(true);

    // Basic material calculation logic
    const squares = totalSquareFeet / 100;
    const wastePercent = 1.1; // 10% waste factor

    const calculatedMaterials = [
      {
        name: "Shingles",
        quantity: Math.ceil(squares * wastePercent * 3), // 3 bundles per square
        unit: "bundles",
        unitPrice: 35,
      },
      {
        name: "Underlayment",
        quantity: Math.ceil(totalSquareFeet / 1000 * wastePercent),
        unit: "rolls",
        unitPrice: 85,
      },
      {
        name: "Starter Strips",
        quantity: Math.ceil(squares * 1.1),
        unit: "bundles",
        unitPrice: 45,
      },
      {
        name: "Ridge Cap",
        quantity: Math.ceil(squares * 0.5),
        unit: "bundles",
        unitPrice: 50,
      },
      {
        name: "Nails",
        quantity: Math.ceil(squares * 0.5),
        unit: "boxes",
        unitPrice: 125,
      },
      {
        name: "Drip Edge",
        quantity: Math.ceil(totalSquareFeet / 120),
        unit: "pieces",
        unitPrice: 12,
      },
      {
        name: "Flashing",
        quantity: Math.ceil(squares * 2),
        unit: "pieces",
        unitPrice: 15,
      },
      {
        name: "Ventilation",
        quantity: Math.ceil(squares / 15),
        unit: "units",
        unitPrice: 35,
      },
    ];

    setMaterials(calculatedMaterials);
    setCalculating(false);
    toast.success("Materials calculated based on roof measurements");
  };

  const handleCreateOrder = async () => {
    if (!supplier.trim()) {
      toast.error("Please enter a supplier name");
      return;
    }

    try {
      const totalCost = materials.reduce(
        (sum, m) => sum + m.quantity * m.unitPrice,
        0
      );

      const { error } = await supabase.from("material_orders").insert({
        job_id: jobId,
        org_id: orgId,
        supplier,
        items: materials,
        total_cost: totalCost,
        status: "draft",
        ai_suggested: true,
      });

      if (error) throw error;

      toast.success("Material order created");
      setMaterials([]);
      setSupplier("");
      onOrderCreated?.();
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create material order");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          Material Calculator
        </CardTitle>
        <CardDescription>
          AI-powered material estimation based on roof measurements
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {totalSquareFeet > 0 ? (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Roof Area</p>
            <p className="text-2xl font-bold">{totalSquareFeet.toLocaleString()} sq ft</p>
            <p className="text-sm text-muted-foreground">
              ({(totalSquareFeet / 100).toFixed(1)} squares)
            </p>
          </div>
        ) : (
          <div className="p-4 bg-muted rounded-lg text-center">
            <Package className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No measurements available. Add roof measurements to calculate materials.
            </p>
          </div>
        )}

        <Button
          onClick={calculateMaterials}
          disabled={calculating || totalSquareFeet <= 0}
          className="w-full gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Calculate Materials
        </Button>

        {materials.length > 0 && (
          <div className="space-y-4">
            <div className="border rounded-lg divide-y">
              {materials.map((material, idx) => (
                <div key={idx} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{material.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {material.quantity} {material.unit} × ${material.unitPrice}
                    </p>
                  </div>
                  <p className="font-semibold">
                    ${(material.quantity * material.unitPrice).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            <div className="p-4 bg-primary/10 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Cost:</span>
                <span className="text-2xl font-bold">
                  ${materials.reduce((sum, m) => sum + m.quantity * m.unitPrice, 0).toFixed(2)}
                </span>
              </div>
            </div>

            <div>
              <Label>Supplier</Label>
              <Input
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder="Enter supplier name"
              />
            </div>

            <Button onClick={handleCreateOrder} className="w-full">
              Create Material Order
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
